"use client";

import { useEffect } from "react";
import Link from "next/link";
import { ArrowRight, Building2, RefreshCw } from "lucide-react";

import { buttonClasses } from "@/components/ui/button";

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Route-segment error boundary for /propiedad/[slug]. Catches:
 *
 *   - Uncaught render errors in this route or its children
 *   - Hydration mismatches (common cause: the page was cached at one
 *     project status, the underlying DB row changed before the user's
 *     stale bfcache page tried to interact)
 *   - Client-side runtime errors from any client component in the tree
 *
 * Renders a friendly fallback instead of Next.js's default
 * "Application error" message. The `reset()` callback re-mounts the
 * route segment, which typically recovers when the underlying cause
 * was transient (cache, network blip).
 *
 * Footer + Navbar are intentionally omitted to keep this lightweight
 * — if a render error happens deep in the tree, we don't want the
 * recovery UI itself to depend on more components that could fail.
 */
export default function PropertyError({ error, reset }: Props) {
  useEffect(() => {
    // Best-effort log to the browser console for the user to inspect.
    // PostHog session replay (if enabled) captures the rest.
    console.error("[propiedad/[slug]/error]", error);
  }, [error]);

  return (
    <main className="bg-cream-50 pb-24 pt-20 sm:pt-28">
      <div className="container-narrow text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral">
          Algo salió mal
        </p>
        <h1 className="mt-4 text-display-sm font-extrabold text-ink sm:text-display">
          No pudimos cargar esta propiedad.
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-ink-500">
          Puede ser un fallo temporal. Intenta recargar — si sigue
          fallando, el problema está de nuestro lado y te invitamos a
          ver el inventario completo mientras lo resolvemos.
        </p>

        <div className="mx-auto mt-10 flex max-w-md flex-col items-stretch gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={reset}
            className={buttonClasses({ variant: "primary", size: "lg" })}
          >
            <RefreshCw className="h-5 w-5" aria-hidden />
            Reintentar
          </button>
          <Link
            href="/propiedades"
            className={buttonClasses({ variant: "secondary", size: "lg" })}
          >
            <Building2 className="h-5 w-5" aria-hidden />
            Ver inventario
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        {error.digest && (
          <p className="mt-12 text-xs text-ink-500">
            ID de error:{" "}
            <code className="rounded bg-ink-100 px-1.5 py-0.5 font-mono">
              {error.digest}
            </code>
          </p>
        )}
      </div>
    </main>
  );
}
